import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import commonStyle from "../../CommonStyles.module.css";
import useApi from "../../../../Utils/BackendClient";
import { buildGroupHierarchy, formatCurrency, formatQuantityWithMeasurement, useEnumLabel } from "../../../../Utils/Utils";
import useGetEnums from "../../../../Utils/EnumsUtils";
import EnumsContext from "../../../../context/enums-context";
import { useLocation, useNavigate } from "react-router-dom";
import DropDownTemplate from "../../../UIElements/FilterTemplates/DropDownTemplate";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { useIsMobile } from "../../../../Utils/ResizeUtils";
import { Sidebar } from "primereact/sidebar";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tree } from "primereact/tree";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { Chip } from "primereact/chip";
import ExternalProductDetailSidebar from "./ExternalProductDetailSidebar";
import InputTextTemplate from "../../../UIElements/FilterTemplates/InputTextTemplate";
import { InputSwitch } from "primereact/inputswitch";

const ExternalProducts = ({ isDarkTheme }) => {

    const ctx = useContext(EnumsContext);
    const isMobile = useIsMobile();
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef();

    const { deleteExternalProduct, fetchExternalProductGroups, fetchExternalProducts } = useApi();

    const [detailsVisible, setDetailsVisible] = useState(false);
    const [externalProducts, setExternalProducts] = useState(null);
    const [lazyState, setlazyState] = useState({
        first: 0,
        rows: 10,
        page: 1,
        sortField: "created_at",
        sortOrder: 1,
        filters: {
            cagent_id: { value: null, matchMode: 'equals' },
            identifier: { value: null, matchMode: 'equals' },
            is_available: { value: null, matchMode: 'equals' },
            name: { value: null, matchMode: 'contains' },
            product_group: { value: null, matchMode: 'equals' },
        }
    });
    const [loading, setLoading] = useState(true);
    const [productCategoriesLoading, setProductCategoriesLoading] = useState(true);
    const [productGroupSidebarVisible, setProductGroupSidebarVisible] = useState(false);
    const [reloadData, setReloadData] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [tableFilters, setTableFilters] = useState(
        {
            cagent_id: { value: null, matchMode: 'equals' },
            identifier: { value: null, matchMode: 'equals' },
            is_available: { value: null, matchMode: 'equals' },
            name: { value: null, matchMode: 'contains' },
            product_group: { value: null, matchMode: 'equals' },
        });
    const [totalRecords, setTotalRecords] = useState(0);
    const [treeHierarchy, setTreeHierarchy] = useState([]);

    const enumTypes = useMemo(() => ["partners", "brands_dictionary"], []);

    useGetEnums(enumTypes);

    const getPartnerLabel = useEnumLabel(ctx.enums.partners.data);

    useEffect(() => {
        const getProductGroups = async () => {
            const response = await fetchExternalProductGroups();

            if (response.status === 200) {

                buildGroupHierarchy(response.data);

                const buildTreeHierarchy = (groups) => {
                    return groups.map(group => {
                        const groupBlock = {
                            key: group.id,
                            label: group.name,
                            droppable: false,
                            children: group?.sub_groups?.length > 0 ? buildTreeHierarchy(group.sub_groups) : undefined,
                        };

                        return groupBlock;
                    });
                };

                const hierarchy = buildTreeHierarchy(response.data || []);

                setTreeHierarchy(hierarchy);
                setProductCategoriesLoading(false);
            }
        }

        getProductGroups();
    }, [fetchExternalProductGroups]);

    useEffect(() => {
        if (location.state?.toast) {
            toast.current.show(location.state.toast);

            location.state.toast = null;
            navigate(location.pathname, { replace: true, state: location.state });
        }

    }, [location.state, location, navigate]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            const response = await fetchExternalProducts(lazyState);

            if (response.status === 200) {

                setTotalRecords(response.data.count);
                setExternalProducts(response.data.results);
            }
            else {
                setExternalProducts([]);
                toast.current.show({ severity: "error", summary: "Помилка завантаження списку актів", detail: "Помилка сервера " + response.status, life: 3000 });
            }
            setLoading(false);
        }

        fetchData();

    }, [reloadData, lazyState, fetchExternalProducts]);

    const EditProductHandler = rowData => {
        setSelectedItem(rowData);
        setDetailsVisible(true);
    }

    const DeleteButtonHandler = async (rowData) => {

        const response = await deleteExternalProduct(rowData.id);

        if (response.status === 204) {
            setReloadData(!reloadData);
            toast.current.show({ severity: "success", summary: 'Видалення', detail: 'Залишок успішно видалено', life: 3000 });
        }
        else {
            toast.current.show({ severity: "error", summary: "Помилка видалення", detail: response.error, life: 3000 });
        }
    }

    const filterApply = () => {
        const editedLazyState = lazyState;
        editedLazyState.page = 1;
        editedLazyState.first = 0;
        editedLazyState.filters = tableFilters;
        setlazyState(editedLazyState);
        setReloadData(!reloadData);
    }

    const clearFilter = (paramName, value) => {
        setlazyState(prevState => ({
            ...prevState,
            filters: {
                ...prevState.filters,
                [paramName]: {
                    ...prevState.filters[paramName],
                    value: value
                }
            }
        }));
    }

    const onPage = (event) => {
        event.page = event.page + 1;
        setlazyState(event);
    };

    const onSort = (event) => {
        event.page = lazyState.page;
        setlazyState(event);
    }

    const onFilter = (event) => {
        event.page = 1;
        setTableFilters(event.filters);
    }

    const applyFilterFromMenu = (node) => {

        const state = lazyState;
        state.page = 1;
        state.first = 0;
        state.filters.product_group.value = node.key;
        state.filters.product_group.name = node.label;
        setlazyState(state);

        const filters = tableFilters;
        filters.product_group.value = node.key;
        filters.product_group.name = node.label;
        setTableFilters(filters);

        setProductGroupSidebarVisible(false);

        setReloadData(!reloadData);
    }

    const clearFilterFromChips = (paramName, value) => {
        clearFilter(paramName, value);
        setTableFilters(prevState => ({
            ...prevState,
            [paramName]: {
                value: value
            }
        }));
    }

    const clearMobileFilters = () => {
        setTableFilters({
            cagent_id: { value: null, matchMode: 'contains' },
            product_group: { value: null, matchMode: 'equals' },
        });

        setlazyState(prevState => ({
            ...prevState,
            filters: {
                cagent_id: { value: null, matchMode: 'contains' },
                product_group: { value: null, matchMode: 'equals' },
            }
        }));
    }

    const nodeTemplate = (node, options) => {
        return (
            <div className="flex justify-content-between align-items-center w-full">
                <div className="flex items-center">
                    <span className="ml-2">{node.label}</span>
                </div>
            </div>
        );
    };

    const toolbarLeftTemplate = () => {
        return (<>{isMobile && <Button icon="pi pi-filter" severity="secondary" outlined={showFilters ? false : true} onClick={() => setShowFilters(!showFilters)} />}
        </>)
    }

    const toolbarRightTemplate = () => {
        return (<div className="flex flex-wrap gap-2">
            {(selectedProducts && selectedProducts.length > 0) && <div className="flex justify-content-center align-items-center mr-3"><label className="bold">Обрано: {selectedProducts.length} товари</label></div>}
            <Button
                label="Створити акт"
                disabled={!selectedProducts || selectedProducts?.length === 0}
                severity="warning"
                className={!selectedProducts || selectedProducts?.length === 0 ? commonStyle.closeButton : commonStyle.warningTag}
                onClick={() => {
                    const route = '/externalacts/newexternalact';
                    navigate(route, { state: { items: selectedProducts } });
                }}
            />
            <Button
                label="Додати залишки"
                severity="success"
                className={commonStyle.addButton}
                onClick={() => navigate("/externalproducts/newexternalproduct")}
            />
        </div>)
    }

    const actionBodyTemplate = (rowData) => {
        return (<div className="flex flex-wrap gap-2">
            <Button
                icon="pi pi-trash"
                className={`p-button-rounded p-button-danger ${commonStyle.deleteButton}`}
                tooltip="Видалити"
                tooltipOptions={{ showOnDisabled: true, position: "top" }}
                size="small"
                onClick={(event) => confirmDelete(event, rowData)}
            />
            <Button
                icon="pi pi-eye"
                className={`p-button-rounded p-button-info ${commonStyle.editButton}`}
                tooltip="Деталі"
                tooltipOptions={{ position: "left" }}
                onClick={() => EditProductHandler(rowData)}
            />
        </div>)
    }

    const confirmDelete = (event, rowData) => {
        confirmDialog({
            group: 'headless',
            header: 'Підтвердження',
            message: 'Підтвердіть видалення залишку контрагента',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: "Так",
            rejectLabel: "Hi",
            defaultFocus: 'accept',
            accept: () => { DeleteButtonHandler(rowData) }
        });
    }

    const imageBodyTemplate = (product) => {
        return (
            <div className={commonStyle.productImageWrapper}>
                <img
                    src={product.logo}
                    alt={product.product_name}
                    className={commonStyle.productImage}
                />
            </div>
        );
    };

    const productGroupFilterBase = () => {
        return (
            <div className={isDarkTheme ? "darkMode" : "lightMode"}>
                <Tree
                    value={treeHierarchy}
                    selectionMode="single"
                    dragdropScope="demo"
                    onDragDrop={(e) => setTreeHierarchy(e.value)}
                    onSelect={e => applyFilterFromMenu(e.node)}
                    className="w-full"
                    filter
                    filterMode="lenient"
                    filterPlaceholder="Пошук групи"
                    nodeTemplate={nodeTemplate}  // Use the nodeTemplate here
                />
            </div>)
    }

    const productGroupFilter = () => {
        if (isMobile) {
            return (
                <Sidebar visible={productGroupSidebarVisible} onHide={() => setProductGroupSidebarVisible(false)} className="w-full">
                    {productGroupFilterBase()}
                </Sidebar>
            )
        }
        else {
            return (<div className={`col-5 md:col-4 xl:col-3 ${productCategoriesLoading ? "flex align-items-center justify-content-center" : ""}`} style={{ height: '100vh' }}>
                {productCategoriesLoading ? (<ProgressSpinner />) : (productGroupFilterBase())}
            </div>);
        }
    }

    const handleMobileFilterChange = (key, value) => {
        setTableFilters((prevFilters) => ({
            ...prevFilters,
            [key]: { ...prevFilters[key], value: value }
        }));
    }

    const formatFilterChips = () => {
        const element = [];

        Object.keys(lazyState.filters).forEach((filterKey) => {
            const filter = lazyState.filters[filterKey];

            if (filter.value && filter.value !== "" && filterKey !== "is_available") {

                let translatedKey = filterKey;
                let changedValue = filter.value;
                let removeValue = null;


                if (filterKey === "name") {
                    translatedKey = "Назва товару";
                    removeValue = "";
                }
                else if (filterKey === "product_group") {
                    translatedKey = "Назва групи";
                    changedValue = filter.name;
                }
                else if (filterKey === "Identifier") {
                    translatedKey = "Ідентифікатор";
                    removeValue = "";
                }

                element.push(
                    <Chip
                        key={filterKey}
                        label={`${translatedKey}: ${changedValue}`}
                        removable
                        onRemove={() => clearFilterFromChips(filterKey, removeValue)} // Optional: To handle removal
                    />
                );
            }
        });

        return <div className="flex flex-wrap gap-2">{element}</div>;
    }

    const OnlyActiveHandler = (value) => {
        const newFilters = { ...tableFilters };
        const newState = { ...lazyState };

        newFilters.is_available = { ...newFilters.is_available, value: value ? true : null };
        newState.page = 1;
        newState.first = 0;
        newState.filters = newFilters;

        setTableFilters(newFilters);
        setlazyState(newState);
        setReloadData(prev => !prev);
    };

    const tableHeader = (
        <div className="w-full">
            <div className="flex justify-content-between align-items-center w-full">
                <div>
                    {!isMobile && formatFilterChips()}
                    {isMobile && (<>
                        <Button
                            icon="pi pi-sitemap"
                            severity="secondary"
                            className='mr-2'
                            outlined
                            onClick={() => setProductGroupSidebarVisible(true)}
                        />
                        <Button
                            icon="pi pi-filter-slash"
                            severity="secondary"
                            outlined
                            onClick={() => clearMobileFilters()}
                        />
                    </>
                    )}
                </div>
                <div className="flex align-items-center gap-2">
                    <InputSwitch checked={tableFilters.is_available.value} onChange={(e) => OnlyActiveHandler(e.value)} />
                    <span>Лише наявні</span>
                </div>
            </div>
            {showFilters && (
                <div className="grid">
                    <div className="col-12 mt-5">
                        <FloatLabel>
                            <InputText
                                value={tableFilters.product_name.value}
                                onChange={(e) =>
                                    handleMobileFilterChange("product_name", e.target.value)
                                }
                                placeholder="Введіть значення"
                                className="p-column-filter w-full"
                            />
                            <label>Назва товару</label>
                        </FloatLabel>
                    </div>
                    <div className="col-12 mt-3">
                        <FloatLabel>
                            <InputText
                                value={tableFilters.product_article.value}
                                onChange={(e) =>
                                    handleMobileFilterChange("product_article", e.target.value)
                                }
                                placeholder="Введіть значення"
                                className="p-column-filter w-full"
                            />
                            <label>Артикул</label>
                        </FloatLabel>
                    </div>
                    <div className="col-12 flex justify-content-center">
                        <Button
                            severity="success"
                            outlined
                            style={{ width: "100%" }}
                            onClick={() => filterApply()}
                            label="Застосувати"
                        />
                    </div>
                </div>
            )}
        </div>
    );

    return (<>
        <Toast ref={toast} />
        <Toolbar className={!isMobile && "mb-4"} start={toolbarLeftTemplate} end={toolbarRightTemplate} />
        <div className="grid">
            {productGroupFilter()}
            <div className='col-9 justify-content-center'>
                <DataTable value={externalProducts} loading={loading} header={tableHeader} stripedRows lazy rows={lazyState.rows} paginator onPage={onPage} selectionMode='checkbox'
                    first={lazyState.first} totalRecords={totalRecords} rowsPerPageOptions={[10, 20, 50]} selection={selectedProducts} onSelectionChange={(e) => setSelectedProducts(e.value)}
                    onSort={onSort} sortField={lazyState.sortField} sortOrder={lazyState.sortOrder} dataKey="id" onFilter={onFilter} filters={lazyState.filters} emptyMessage="Товарів не знайдено">
                    <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                    <Column field="logo" body={imageBodyTemplate} style={{ width: '5%' }} />
                    <Column field="name" header="Товар" filter filterElement={InputTextTemplate} onFilterApplyClick={filterApply} onFilterClear={() => clearFilter("name", "")}
                        showFilterMatchModes={false} />
                    <Column field="quantity" header="Кількість" body={(rowData) => formatQuantityWithMeasurement(rowData.quantity, rowData.measurement_units)} style={{ width: '10%' }} />
                    <Column field="identifier" header="Ідентифікатор" filter filterElement={InputTextTemplate} onFilterApplyClick={filterApply} onFilterClear={() => clearFilter("identtifier", "")}
                        showFilterMatchModes={false} />
                    <Column field="cagent_id" header="Постачальник" sortable filter filterElement={(options) => DropDownTemplate(options, ctx.enums.partners.data)} onFilterApplyClick={filterApply}
                        onFilterClear={() => clearFilter("cagent_id", null)} showFilterMatchModes={false} body={rowData => getPartnerLabel(rowData.cagent_id)} />
                    <Column field="price" header="Ціна" body={rowData => formatCurrency(rowData.price, rowData.currency)} />
                    <Column field="action" body={(rowData) => actionBodyTemplate(rowData)} style={{ width: '8%' }} />
                </DataTable>
            </div>
            <ConfirmDialog group="headless" />
            <ExternalProductDetailSidebar visible={detailsVisible} onHide={() => setDetailsVisible(false)} toast={toast} ctx={ctx} data={selectedItem} />
        </div>
    </>);
}

export default ExternalProducts;